import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import CourseSidebar from './components/CourseSidebar';
import CourseItemList from './components/CourseItemsList';
import FooterTwo from '../../components/FooterTwo';
import ApplyModal from './components/ApplyModal';
import CoursePagination from './components/CoursePagination';
import PopularCourse from './components/PopularCourse';
import { COURSES_ENDPOINT, APPLICATIONS_ENDPOINT, COURSE_FILTERS_ENDPOINT, APPLICATIONS_RECENT_ENDPOINT } from '../../config/api';
import { Styles } from './styles/course.js';
import { useAuth } from '../../context/AuthContext';

const PAGE_SIZE = 8;
const initialApplicationStatus = { state: 'idle', message: '' };
const createInitialFilters = () => ({
    categories: [],
    price: '',
    tags: []
});

const CourseList = () => {
    const { user } = useAuth();
    const [courses, setCourses] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 0, hasMore: false });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentSearch, setCurrentSearch] = useState('');
    const [filters, setFilters] = useState(createInitialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersData, setFiltersData] = useState({
        categories: [],
        priceBuckets: [],
        popularCourses: [],
        tags: []
    });
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [filtersError, setFiltersError] = useState('');
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [applicationStatus, setApplicationStatus] = useState(initialApplicationStatus);
    const [recentApplicants, setRecentApplicants] = useState({});
    const [recentApplicantsLoading, setRecentApplicantsLoading] = useState(false);
    const [appliedCourseIds, setAppliedCourseIds] = useState(() => new Set());

    const fetchCourses = useCallback(async (searchTerm = '', appliedFilters, page = 1) => {
        setLoading(true);
        setError('');
        try {
            const activeFilters = appliedFilters ?? createInitialFilters();
            const params = new URLSearchParams();
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            if (activeFilters.categories?.length) {
                params.append('categories', activeFilters.categories.join(','));
            }
            if (activeFilters.price) {
                params.append('price', activeFilters.price);
            }
            if (activeFilters.tags?.length) {
                params.append('tags', activeFilters.tags.join(','));
            }
            if (page && page > 1) {
                params.append('page', page);
            }
            params.append('limit', PAGE_SIZE);
            const query = params.toString();
            const response = await fetch(`${COURSES_ENDPOINT}${query ? `?${query}` : ''}`);
            if (!response.ok) {
                throw new Error('Unable to load courses right now. Please try again later.');
            }
            const result = await response.json();
            const fetchedCourses = result.courses || [];
            const metaData = result.meta || {
                total: fetchedCourses.length,
                page: 1,
                pageSize: PAGE_SIZE,
                hasMore: false
            };
            if (!metaData.pageSize) {
                metaData.pageSize = PAGE_SIZE;
            }
            setCourses(fetchedCourses);
            setMeta(metaData);
            if (metaData.page && metaData.page !== page) {
                setCurrentPage(metaData.page);
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Something went wrong while loading courses.');
            setCourses([]);
            setMeta({ total: 0, page: 1, pageSize: 0, hasMore: false });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCourses(currentSearch, filters, currentPage);
    }, [fetchCourses, currentSearch, filters, currentPage]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const loadApplications = async () => {
            if (!user?.email) {
                if (isMounted) {
                    setAppliedCourseIds(new Set());
                }
                return;
            }

            try {
                const response = await fetch(`${APPLICATIONS_ENDPOINT}?email=${encodeURIComponent(user.email)}`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error('Unable to fetch applications');
                }

                const payload = await response.json();
                if (!isMounted) {
                    return;
                }

                const nextIds = new Set();
                (payload.applications || []).forEach((application) => {
                    const courseRef = application.course || application.courseId;
                    const courseId =
                        (courseRef && typeof courseRef === 'object' && (courseRef._id || courseRef.id)) ||
                        (typeof courseRef === 'string' ? courseRef : null) ||
                        application.course;
                    if (courseId) {
                        nextIds.add(String(courseId));
                    }
                });

                setAppliedCourseIds(nextIds);
            } catch (err) {
                if (!controller.signal.aborted && isMounted) {
                    setAppliedCourseIds(new Set());
                }
            }
        };

        loadApplications();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [user?.email]);

    useEffect(() => {
        const controller = new AbortController();

        const fetchFilters = async () => {
            setFiltersLoading(true);
            setFiltersError('');
            try {
                const params = new URLSearchParams();
                if (currentSearch) {
                    params.append('search', currentSearch);
                }
                if (filters.categories?.length) {
                    params.append('categories', filters.categories.join(','));
                }
                if (filters.price) {
                    params.append('price', filters.price);
                }
                if (filters.tags?.length) {
                    params.append('tags', filters.tags.join(','));
                }

                const query = params.toString();
                const response = await fetch(`${COURSE_FILTERS_ENDPOINT}${query ? `?${query}` : ''}`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error('Unable to load course filters right now.');
                }

                const data = await response.json();
                if (!controller.signal.aborted) {
                    setFiltersData({
                        categories: data.categories || [],
                        priceBuckets: data.priceBuckets || [],
                        popularCourses: data.popularCourses || [],
                        tags: data.tags || []
                    });
                }
            } catch (err) {
                if (controller.signal.aborted) {
                    return;
                }
                console.error(err);
                setFiltersData({ categories: [], priceBuckets: [], popularCourses: [], tags: [] });
                setFiltersError(err.message || 'Unable to load course filters.');
            } finally {
                if (!controller.signal.aborted) {
                    setFiltersLoading(false);
                }
            }
        };

        fetchFilters();

        return () => controller.abort();
    }, [currentSearch, filters]);

    useEffect(() => {
        if (!Array.isArray(courses) || courses.length === 0) {
            setRecentApplicants({});
            setRecentApplicantsLoading(false);
            return;
        }

        const courseIds = courses
            .map((course) => course?._id)
            .filter((id) => typeof id === 'string' && id.trim());

        if (courseIds.length === 0) {
            setRecentApplicants({});
            setRecentApplicantsLoading(false);
            return;
        }

        const controller = new AbortController();
        const params = new URLSearchParams({ courseIds: courseIds.join(',') });

        const fetchRecentApplicants = async () => {
            setRecentApplicantsLoading(true);
            try {
                const response = await fetch(`${APPLICATIONS_RECENT_ENDPOINT}?${params.toString()}`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error('Unable to load recent applicants.');
                }

                const payload = await response.json();
                if (!controller.signal.aborted) {
                    setRecentApplicants(payload.courses || {});
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.error(err);
                    setRecentApplicants({});
                }
            } finally {
                if (!controller.signal.aborted) {
                    setRecentApplicantsLoading(false);
                }
            }
        };

        fetchRecentApplicants();

        return () => controller.abort();
    }, [courses]);

    const handleSearch = (searchTerm) => {
        setCurrentSearch(searchTerm);
        setCurrentPage(1);
    };

    const handleToggleCategory = (category) => {
        setFilters((prev) => {
            const exists = prev.categories.includes(category);
            const nextCategories = exists
                ? prev.categories.filter((item) => item !== category)
                : [...prev.categories, category];
            return { ...prev, categories: nextCategories };
        });
        setCurrentPage(1);
    };

    const handleTogglePrice = (priceBucket) => {
        setFilters((prev) => {
            const normalized = priceBucket === 'all' ? '' : priceBucket;
            const nextPrice = prev.price === normalized ? '' : normalized;
            return { ...prev, price: nextPrice };
        });
        setCurrentPage(1);
    };

    const handleToggleTag = (tag) => {
        setFilters((prev) => {
            const exists = prev.tags.includes(tag);
            const nextTags = exists ? prev.tags.filter((item) => item !== tag) : [...prev.tags, tag];
            return { ...prev, tags: nextTags };
        });
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters(createInitialFilters());
        setCurrentPage(1);
    };

    const handleApplyClick = (course) => {
        const courseId = course?._id ? String(course._id) : '';
        if (!courseId || appliedCourseIds.has(courseId)) {
            return;
        }
        setSelectedCourse(course);
        setApplicationStatus(initialApplicationStatus);
        setShowApplyModal(true);
    };

    const handleCloseApplyModal = () => {
        setShowApplyModal(false);
        setSelectedCourse(null);
        setApplicationStatus(initialApplicationStatus);
    };

    const handleApplicationSubmit = async (payload) => {
        if (!selectedCourse) {
            return;
        }

        setApplicationStatus({ state: 'submitting', message: '' });
        try {
            const response = await fetch(APPLICATIONS_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    courseId: selectedCourse._id,
                    ...payload
                })
            });

            if (!response.ok) {
                const { message } = await response.json();
                throw new Error(message || 'Unable to submit your application.');
            }

            setApplicationStatus({ state: 'success', message: 'Application submitted successfully.' });
            setAppliedCourseIds((prev) => {
                const next = new Set(prev);
                if (selectedCourse?._id) {
                    next.add(String(selectedCourse._id));
                }
                return next;
            });
        } catch (err) {
            console.error(err);
            setApplicationStatus({
                state: 'error',
                message: err.message || 'We could not submit your application. Please try again.'
            });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="main-wrapper course-page">

            <HeaderTwo />

            <BreadcrumbBox title="Courses" />

            <Styles>
                <section className="course-grid-area">
                    <Container>
                        <Row>
                            <Col lg="3" md="12">
                                <CourseSidebar
                                    onSearch={handleSearch}
                                    loading={loading}
                                    totalResults={meta.total}
                                    currentSearch={currentSearch}
                                    selectedCategories={filters.categories}
                                    selectedPrice={filters.price}
                                    selectedTags={filters.tags}
                                    onToggleCategory={handleToggleCategory}
                                    onTogglePrice={handleTogglePrice}
                                    onToggleTag={handleToggleTag}
                                    onClearFilters={handleClearFilters}
                                    filtersLoading={filtersLoading}
                                    filtersError={filtersError}
                                    categories={filtersData.categories}
                                    priceBuckets={filtersData.priceBuckets}
                                    tags={filtersData.tags}
                                />
                            </Col>
                            <Col lg="6" md="12">
                                {error && !loading && (
                                    <Alert variant="danger">{error}</Alert>
                                )}
                                <div className="course-items">
                                    <Row>
                                        <CourseItemList
                                            loading={loading}
                                            error={error}
                                            courses={courses}
                                            onApply={handleApplyClick}
                                            recentApplicants={recentApplicants}
                                            recentApplicantsLoading={recentApplicantsLoading}
                                            appliedCourseIds={appliedCourseIds}
                                        />
                                    </Row>
                                </div>
                                <CoursePagination
                                    meta={meta}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    loading={loading}
                                />
                            </Col>
                            <Col lg="3" md="12">
                                <PopularCourse
                                    loading={filtersLoading}
                                    courses={filtersData.popularCourses}
                                />
                            </Col>
                        </Row>
                    </Container>
                </section>
            </Styles>

            <FooterTwo />

            <ApplyModal
                show={showApplyModal}
                course={selectedCourse}
                status={applicationStatus}
                onClose={handleCloseApplyModal}
                onSubmit={handleApplicationSubmit}
            />

        </div>
    );
};

export default CourseList;
